export enum UserStatus {
  GUEST = 'GUEST',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SUSPENDED = 'SUSPENDED',
  BANNED = 'BANNED',
}

export interface User {
  id: string;
  name: string;
  userName?: string;
  email: string;
  role?: string;
  birthDate?: Date;
  status: UserStatus;
  job?: string;
  location?: string;
  interests: string[];
  preferredAge?: 'older' | 'younger' | 'any';
  hearts: number;
  profileImageUrl?: string;
  createdAt: Date;
  isKakaoLogin: boolean;
  kakaoVerified: boolean;
}

export interface CupidCard {
  id: string;
  cardId: number;
  status: 'LOCKED' | 'OPENED' | 'EXPIRED';
  targetUserId: string;
  targetUser: {
    name: string;
    age: number;
    location: string;
    interests: string[];
    profileImageUrl?: string;
  };
  issuedAt: Date;
  expiresAt: Date;
  extendDeadlineAt: string;
  isOpened: boolean;
  openedTime?: string;
  extendCount: number;
  isExtendable: boolean;
  isExpiredExtendable: boolean;
  isDead: boolean;
}

export interface Chat {
  id: string;
  cardId: string;
  participants: string[];
  messages: Message[];
  createdAt: Date;
}

export interface Message {
  id: string;
  chatId: string;
  senderId: string;
  content: string;
  timestamp: Date;
}

export interface HeartPurchase {
  id: string;
  userId: string;
  amount: number;
  paymentMethod: 'toss' | 'naver' | 'kakao';
  status: 'pending' | 'completed' | 'failed';
  createdAt: Date;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface TokenDto {
  accessToken: string;
  refreshToken?: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
}

export interface ApiLoginResponse {
  userInfo: User;
  token: TokenDto;
}

export interface ApiJoinResponse {
  userInfo: User;
  tokenDto: TokenDto;
}

export interface CompleteProfileRequest {
  birthDate: string;
  gender: string;
  job: string;
  location: string;
  interests: string[];
  selfIntroduction: string;
  preferredAge: string;
}

export interface CompleteProfileResponse {
  requestedUserDto: User;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password?: string;
  birthDate: Date;
  job: string;
  location: string;
  interests: string[];
  preferredAge: 'older' | 'younger' | 'any';
  profileImageUrl?: string;
}

export interface KakaoRegistrationRequest {
  birthDate: Date;
  job: string;
  location: string;
  interests: string[];
  preferredAge: 'older' | 'younger' | 'any';
  profileImageUrl?: string;
}

export interface KakaoLoginRequest {
  code: string;
  redirectUri: string;
}

export interface UpdateUserRequest {
  name?: string;
  job?: string;
  location?: string;
  interests?: string[];
  preferredAge?: 'older' | 'younger' | 'any';
  profileImageUrl?: string;
}

export interface GetCupidCardsResponse {
  cards: CupidCard[];
  totalCount: number;
  hasMore: boolean;
}

export interface OpenCardRequest {
  cardId: string;
}

export interface ExtendCardRequest {
  cardId: string;
  days: number;
}

export interface CreateChatRequest {
  cardId: string;
  message: string;
}

export interface GetChatMessagesRequest {
  chatId: string;
  page?: number;
  limit?: number;
}

export interface SendMessageRequest {
  chatId: string;
  content: string;
}

// Legacy PurchaseHeartsRequest removed - see 서버 PurchaseHeartsRequest below

export interface UploadImageResponse {
  imageUrl: string;
}

export interface GetUserStatsResponse {
  totalCards: number;
  openedCards: number;
  activeChats: number;
  hearts: number;
  totalMatches: number;
}

// 새로운 프로필 등록 관련 타입들
export type PreferredAge = "YOUNGER" | "OLDER" | "ANY";

export interface RegionDto {
  level1Id: number;
  level1Name: string;
  level2Id: number;
  level2Name: string;
}

// 프로필 등록 시 사용하는 확장 타입
export interface PreferredRegionDto extends RegionDto {
  level1Name: string;
  level2Name: string;
}

export interface ProfileImageDto {
  storageKey: string;
  mimeType: string;
  size: number; // bytes
}

export interface SilverConnectRegisterProfileRequest {
  birthDate: string; // yyyy-MM-dd
  gender: string;
  jobTitle?: string | null;
  residenceRegion: RegionDto;
  oneLiner?: string | null;  // 1~64
  selfIntro?: string | null; // 10~512
  preferredAge: PreferredAge;
  interestIds?: number[]; // default []
  preferredRegions?: RegionDto[]; // default []
  profileImages: ProfileImageDto[]; // 정책상 3~10개
}

export interface SilverConnectUpdateProfileRequest {
  birthDate: string; // yyyy-MM-dd
  gender: string;
  jobTitle?: string | null;
  residenceRegion: RegionDto;
  oneLiner?: string | null;  // 1~64
  selfIntro?: string | null; // 10~512
  preferredAge: PreferredAge;
  interestIds?: number[]; // default []
  preferredRegions?: RegionDto[]; // default []
  profileImages: ProfileImageDto[]; // 새로 추가된 이미지들
  deletedStorageKeys: string[]; // 삭제할 기존 이미지의 S3 키들
}

// 메타 정보 타입들
export interface Interest {
  id: number;
  code: string;
  labelKo: string;
  isActive: boolean;
  sortOrder: number;
}

export interface InterestsResponse {
  result: {
    interests: Interest[];
  };
  resultType: string;
}

export interface Region {
  id: number;
  nameKo: string;
  level: number;
  parentId: number | null;
  sortOrder: number;
  isActive: boolean;
}

export interface RegionsResponse {
  result: {
    regions: Region[];
  };
  resultType: string;
}

// Presigned URL 응답 타입
export interface PresignedUrlResponse {
  url: string;
  fields: { [key: string]: string };
}

// 프로필 등록 응답 타입
export interface ProfilePhoto {
  id: number;
  endpointUrl: string;
  sortOrder: number;
  createdTime: string | null;
}

export interface ProfileInterest {
  id: number;
  name: string;
}

export interface ProfileRegistrationResult {
  id: number;
  userId: number;
  birthDate: string;
  gender: string;
  jobTitle: string | null;
  residenceRegion: RegionDto;
  oneLiner: string | null;
  selfIntro: string | null;
  reviewStatus: string;
  submittedTime: string;
  reviewedTime: string | null;
  reviewerUserId: number | null;
  rejectReason: string | null;
  preferredAge: PreferredAge;
  profilePhotos: ProfilePhoto[];
  interests: ProfileInterest[];
  preferredRegions: RegionDto[];
  createdTime: string;
  updatedTime: string;
}

export interface ProfileRegistrationResponse {
  result: ProfileRegistrationResult;
  resultType: 'SUCCESS' | 'ERROR';
  error?: {
    errorType: string;
    message: string;
  };
}

// 관리자 API 타입들
export enum ReviewStatus {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  UPDATED = 'UPDATED',
  REJECTED = 'REJECTED'
}

export interface CursorPageDto<T> {
  content: T[];
  nextCursor: string | null;
  hasNext: boolean;
  size: number;
}

export interface AdminRegionDto {
  level1Id: number;
  level1Name: string;
  level2Id: number;
  level2Name: string;
}

export interface SilverConnectAdminUserListDto {
  id: number;
  userId: number;
  name: string;
  email: string;
  userStatus: UserStatus | null;
  profileImageUrl: string | null;
  reviewStatus: ReviewStatus;
  submittedTime: string | null;
  lastLoginTime: string | null;
}

export interface InterestDto {
  id: number;
  name: string;
}

export interface ProfilePhotoDto {
  id: number;
  endpointUrl: string;
  sortOrder: number;
  createdTime: string | null;
}

export interface SilverConnectAdminUserDetailDto {
  id: number;
  userId: number;
  name: string;
  email: string;
  phoneNumber: string | null;
  userStatus: UserStatus | null;
  profileImageUrl: string | null;
  joinedTime: string | null;
  lastLoginTime: string | null;
  birthDate: string | null;
  gender: string | null;
  jobTitle: string | null;
  residenceRegion: AdminRegionDto | null;
  oneLiner: string | null;
  selfIntro: string | null;
  preferredAge: PreferredAge | null;
  reviewStatus: ReviewStatus;
  submittedTime: string | null;
  reviewedTime: string | null;
  reviewerUserId: number | null;
  rejectReason: string | null;
  profilePhotos: ProfilePhotoDto[];
  interests: InterestDto[];
  preferredRegions: AdminRegionDto[];
  resubmissionCount: number;
  createdTime: string | null;
  updatedTime: string | null;
}

export interface UpdateUserStatusRequest {
  reviewStatus: ReviewStatus;
  rejectReason?: string;
  rejectReasonCodes?: string[];
}

export interface AdminApiResponse<T> {
  result: T;
  resultType: 'SUCCESS' | 'ERROR';
  error?: {
    errorType: string;
    message: string;
  };
}

// 하트 시스템 타입들

// 서버 HeartBalanceDto 매칭
export interface HeartBalance {
  balance: number;
}

// 서버 HeartWalletDto 매칭
export interface HeartWallet {
  userId: number;
  balance: number;
  createdTime: string;
  updatedTime: string;
}

// 서버 HeartPackageDto 매칭
export interface HeartPackage {
  id: number;
  name: string;
  heartAmount: number;
  bonusAmount: number;
  totalHearts: number;
  price: number;
  discountRate: number;
  pricePerHeart: number;
  description?: string;
  isPopular?: boolean;
  isBestValue?: boolean;
}

// 서버 HeartTransactionDto 매칭
export interface HeartTransaction {
  id: number;
  transactionType: string;
  transactionTypeTitle: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  reason?: string;
  referenceType?: string;
  referenceId?: number;
  createdTime: string;
}

// 서버 HeartTransactionPageDto 매칭
export interface GetTransactionsResponse {
  transactions: HeartTransaction[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
}

export interface GetTransactionsRequest {
  page?: number;
  size?: number;
  type?: string;
}

// 서버 PurchasePackageRequest 매칭
export interface PurchaseHeartPackageRequest {
  packageId: number;
  paymentMethod?: string;
  paymentReferenceId?: string;
}

// 서버 PurchasePackageResponse 매칭
export interface PurchaseHeartPackageResponse {
  success: boolean;
  message: string;
  packageName: string;
  heartAmount: number;
  bonusAmount: number;
  totalHearts: number;
  currentBalance: number;
}

// 서버 PurchaseHeartsRequest 매칭
export interface PurchaseHeartsRequest {
  amount: number;
  paymentReferenceId?: string;
}

// 서버 PurchaseHeartsDto 매칭
export interface PurchaseHeartsResponse {
  success: boolean;
  message: string;
  purchasedHearts: number;
  currentBalance: number;
}

// 서버 GrantRewardRequest 매칭
export interface GrantRewardRequest {
  rewardType: string;
  description?: string;
}

// 서버 GrantRewardResponse 매칭
export interface GrantRewardResponse {
  success: boolean;
  message: string;
  rewardType: string;
  rewardTypeTitle: string;
  amount: number;
  currentBalance: number;
}

// 서버 DailyFreeHeartsDto 매칭
export interface DailyFreeHeartsResponse {
  success: boolean;
  message: string;
  earnedHearts: number;
  currentBalance: number;
}

// 서버 RewardPolicyDto 매칭
export interface RewardPolicyDto {
  id: number;
  rewardType: string;
  rewardTypeTitle: string;
  amount: number;
  maxCount?: number;
  period: string;
  periodTitle: string;
  description?: string;
}

// 서버 RewardAvailabilityDto 매칭
export interface RewardAvailabilityDto {
  rewardType: string;
  canGrant: boolean;
  amount: number;
  reason?: string;
}

// 서버 RewardHistoryDto 매칭
export interface RewardHistoryDto {
  id: number;
  rewardType: string;
  rewardTypeTitle: string;
  amount: number;
  description?: string;
  createdTime: string;
}

// 서버 ConsumeHeartsRequest 매칭
export interface ConsumeHeartsRequest {
  transactionType: string;
  referenceId?: number;
}

// 서버 ConsumeHeartsResponse 매칭
export interface ConsumeHeartsResponse {
  success: boolean;
  message: string;
  consumedHearts: number;
  currentBalance: number;
}

// ─── Admin Cupid Card Types ──────────────────────────────────────────────────

export interface AdminUserSummaryDto {
  userId: number;
  name: string;
  age: number | null;
  region: string | null;
}

export interface HeartTransactionSummaryDto {
  transactionType: string;
  amount: number;
  createdTime: string; // ISO datetime string
}

export interface CupidCardAdminStatsDto {
  batchWeek: string;
  totalCards: number;
  lockedCount: number;
  openedCount: number;
  openedRate: number;
  expiredExtendableCount: number;
  deadCount: number;
  heartConsumedTotal: number;
  extendCount: number;
  extendRate: number;
}

export interface CupidCardBatchResultDto {
  batchWeek: string;
  executedAt: string | null;
  endedAt: string | null;
  batchStatus: string | null; // "COMPLETED" | "FAILED" | "NOT_EXECUTED"
  recipientCount: number;
  issuedCardCount: number;
  failedRecipientCount: number;
}

export interface CupidCardAdminListDto {
  cardId: number;
  status: string; // "LOCKED" | "OPENED" | "EXPIRED"
  recipientUserId: number;
  recipientName: string;
  targetUserId: number;
  targetName: string;
  issuedAt: string;
  expiresAt: string;
  extendDeadlineAt: string;
  openedTime: string | null;
  extendCount: number;
  isExpiredExtendable: boolean;
  isDead: boolean;
}

export interface CupidCardAdminDetailDto {
  cardId: number;
  status: string;
  isExpiredExtendable: boolean;
  isDead: boolean;
  recipient: AdminUserSummaryDto;
  target: AdminUserSummaryDto;
  issuedAt: string;
  expiresAt: string;
  extendDeadlineAt: string;
  openedTime: string | null;
  extendCount: number;
  heartTransactions: HeartTransactionSummaryDto[];
}

export interface HeartGrantResultDto {
  userId: number;
  grantedAmount: number;
  balanceBefore: number;
  balanceAfter: number;
  reason: string;
}

// 큐피트 카드 수동 발급
export interface ManualIssueCardRequest {
  recipientUserId: number;
  targetUserId: number;
  issuedAt?: string;       // 선택 (미입력 시 서버에서 현재 시각 사용)
  expiresAt: string;       // 필수 (ISO datetime, e.g. "2026-02-21T11:00:00")
  extendDeadlineAt: string; // 필수 (ISO datetime, e.g. "2026-02-24T11:00:00")
}

export interface ManualIssueCardResultDto {
  cardId: number;
  status: string;
  isExpiredExtendable: boolean;
  isDead: boolean;
  recipient: { userId: number; name: string };
  target: { userId: number; name: string };
  issuedAt: string;
  expiresAt: string;
  extendDeadlineAt: string;
  openedTime: string | null;
  extendCount: number;
  heartTransactions: Array<{ transactionType: string; amount: number; createdTime: string }>;
}

export interface AdminCupidCardPageDto {
  content: CupidCardAdminListDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number; // current page (0-indexed)
}